import React, { useState, useMemo } from 'react';
import { 
  Plus, Search, Filter, Printer, Download, Eye, Edit2, Trash2, Copy, 
  CheckCircle2, DollarSign, FileText, Building, Calendar, ArrowUpDown, 
  MoreVertical, Check, AlertCircle, Clock, ShieldAlert, Sparkles
} from 'lucide-react';
import { useLanguage } from '../../../context/LanguageContext';
import { useCollection } from '../../../hooks/useCollection';
import { printInvoice } from '../utils/printInvoice';
import { formatDateFR } from '../../shared/utils/helpers';
import ManualInvoiceModal from './ManualInvoiceModal';
import InvoicePreviewModal from './InvoicePreviewModal';
import RecordPaymentModal from './RecordPaymentModal';

export default function InvoicesPage({ settings = {} }) {
  const { lang, t } = useLanguage();
  const isAr = lang === 'ar';
  const cur = settings?.currency || 'MAD';

  // Collections
  const { data: manualInvoices = [], loading: loadingManual, add: addManual, update: updateManual, remove: removeManual } = useCollection('invoices');
  const { data: rentals = [], loading: loadingRentals, update: updateRental } = useCollection('rentals');
  const { data: clients = [] } = useCollection('clients');

  // Modals state
  const [showManualModal, setShowManualModal] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState(null);
  const [previewInvoice, setPreviewInvoice] = useState(null);
  const [paymentInvoice, setPaymentInvoice] = useState(null);

  // Filters & Tabs state
  const [activeTab, setActiveTab] = useState('all'); // 'all' | 'manual' | 'rentals'
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all'); // 'all' | 'draft' | 'issued' | 'paid' | 'partial' | 'unpaid'
  const [sortBy, setSortBy] = useState('date_desc'); // 'date_desc' | 'date_asc' | 'amount_desc' | 'amount_asc'

  // Standardize and merge manual and rental invoices for a unified experience
  const allUnifiedInvoices = useMemo(() => {
    // 1. Process Manual Invoices
    const processedManual = (manualInvoices || []).map(inv => {
      const totalTTC = Number(inv.totalTTC || inv.totalAmount) || 0;
      const paid = Number(inv.paidAmount) || 0;
      const remaining = Math.max(0, totalTTC - paid);
      const date = inv.invoiceDate || inv.date || (inv.createdAt ? String(inv.createdAt).split('T')[0] : '');

      return {
        ...inv,
        isManual: true,
        sourceType: 'manual',
        invoiceNumber: inv.invoiceNumber || (inv.id ? String(inv.id).substring(0, 8).toUpperCase() : 'FAC-0000'),
        clientOrCompanyName: inv.companyName || inv.clientName || '—',
        date,
        dueDate: inv.dueDate || '',
        totalTTC,
        paidAmount: paid,
        remainingAmount: remaining,
        status: inv.status || (remaining === 0 ? 'paid' : 'issued'),
        paymentMethod: inv.paymentMethod || 'Espèces'
      };
    });

    // 2. Process Rental Invoices
    const processedRentals = (rentals || []).map(r => {
      const rentalAmount = Number(r.totalAmount) || 0;
      const depositAmount = Number(r.deposit) || 0;
      const totalTTC = rentalAmount + depositAmount;
      const isPaid = r.status === 'completed' || r.isPaid;
      const paid = isPaid ? totalTTC : (Number(r.paidAmount) || 0);
      const remaining = Math.max(0, totalTTC - paid);
      const date = r.startDate || (r.createdAt ? String(r.createdAt).split('T')[0] : '');

      let status = 'issued';
      if (remaining === 0 || isPaid) status = 'paid';
      else if (paid > 0) status = 'partial';

      return {
        ...r,
        isManual: false,
        sourceType: 'rental',
        invoiceNumber: (r.id || '').substring(0, 8).toUpperCase(),
        clientOrCompanyName: r.clientName || '—',
        date,
        dueDate: r.endDate || '',
        totalTTC,
        paidAmount: paid,
        remainingAmount: remaining,
        status,
        paymentMethod: r.paymentMethod || 'Espèces'
      };
    });

    if (activeTab === 'manual') return processedManual;
    if (activeTab === 'rentals') return processedRentals;
    return [...processedManual, ...processedRentals];
  }, [manualInvoices, rentals, activeTab]);

  // Filtered & Sorted Invoices
  const filteredInvoices = useMemo(() => {
    let result = allUnifiedInvoices;

    // Search query filter
    if (searchTerm.trim()) {
      const q = searchTerm.toLowerCase().trim();
      result = result.filter(inv => 
        (inv.invoiceNumber || '').toLowerCase().includes(q) ||
        (inv.clientOrCompanyName || '').toLowerCase().includes(q) ||
        (inv.companyPhone || inv.clientPhone || '').includes(q) ||
        (inv.companyICE || inv.ice || '').toLowerCase().includes(q) ||
        (inv.carName || '').toLowerCase().includes(q)
      );
    }

    // Status filter
    if (statusFilter !== 'all') {
      result = result.filter(inv => inv.status === statusFilter);
    }

    // Sorting
    return [...result].sort((a, b) => {
      if (sortBy === 'date_desc') return new Date(b.date || 0) - new Date(a.date || 0);
      if (sortBy === 'date_asc') return new Date(a.date || 0) - new Date(b.date || 0);
      if (sortBy === 'amount_desc') return (b.totalTTC || 0) - (a.totalTTC || 0);
      if (sortBy === 'amount_asc') return (a.totalTTC || 0) - (b.totalTTC || 0);
      return 0;
    });
  }, [allUnifiedInvoices, searchTerm, statusFilter, sortBy]);

  // Overall KPI Statistics
  const stats = useMemo(() => {
    let totalInvoiced = 0;
    let totalCollected = 0;
    let totalPending = 0;
    let countPaid = 0;
    let countPartial = 0;
    let countUnpaid = 0;

    allUnifiedInvoices.forEach(inv => {
      totalInvoiced += (inv.totalTTC || 0);
      totalCollected += (inv.paidAmount || 0);
      totalPending += (inv.remainingAmount || 0);

      if (inv.status === 'paid') countPaid++;
      else if (inv.status === 'partial') countPartial++;
      else countUnpaid++;
    });

    return {
      totalInvoiced,
      totalCollected,
      totalPending,
      totalCount: allUnifiedInvoices.length,
      countPaid,
      countPartial,
      countUnpaid
    };
  }, [allUnifiedInvoices]);

  // Actions
  const handleSaveManualInvoice = async (payload) => {
    if (editingInvoice?.id) {
      await updateManual(editingInvoice.id, payload);
    } else {
      await addManual(payload);
    }
  };

  const handleDeleteInvoice = async (inv) => {
    if (!inv.isManual) {
      return alert(isAr ? 'فواتير الكراء مرتبطة بعقد الكراء. يمكنك حذفها من صفحة الكراء.' : 'Les factures de location sont liées à un contrat. Vous pouvez les gérer depuis la page Locations.');
    }
    if (window.confirm(t('confirm_delete_invoice') || (isAr ? 'هل أنت متأكد من حذف هذه الفاتورة؟' : 'Êtes-vous sûr de vouloir supprimer cette facture ?'))) {
      try {
        await removeManual(inv.id);
      } catch (err) {
        alert('❌ ' + err.message);
      }
    }
  };

  const handleDuplicateInvoice = async (inv) => {
    try {
      const year = new Date().getFullYear();
      const newNumber = `FAC-${year}-${String((manualInvoices?.length || 0) + 1).padStart(4, '0')}`;
      
      const duplicatedData = {
        ...inv,
        id: undefined,
        _id: undefined,
        invoiceNumber: newNumber,
        invoiceDate: new Date().toISOString().split('T')[0],
        status: 'draft',
        paidAmount: 0,
        remainingAmount: inv.totalTTC,
        paymentHistory: [],
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      await addManual(duplicatedData);
      alert(t('invoice_duplicated_success') || (isAr ? 'تم نسخ الفاتورة بنجاح!' : 'Facture dupliquée avec succès !'));
    } catch (err) {
      alert('❌ ' + err.message);
    }
  };

  const handleMarkAsPaid = async (inv) => {
    if (window.confirm(t('confirm_mark_paid') || (isAr ? 'هل تريد تحديد هذه الفاتورة كمدفوعة بالكامل؟' : 'Marquer cette facture comme totalement payée ?'))) {
      try {
        if (inv.isManual) {
          await updateManual(inv.id, {
            status: 'paid',
            paidAmount: inv.totalTTC,
            remainingAmount: 0,
            updatedAt: new Date().toISOString()
          });
        } else {
          await updateRental(inv.id, {
            status: 'completed',
            isPaid: true,
            paidAmount: inv.totalTTC
          });
        }
      } catch (err) {
        alert('❌ ' + err.message);
      }
    }
  };

  const handleRecordPaymentSave = async (updatedInvoice) => {
    if (updatedInvoice.isManual) {
      await updateManual(updatedInvoice.id, updatedInvoice);
    } else {
      await updateRental(updatedInvoice.id, {
        paidAmount: updatedInvoice.paidAmount,
        isPaid: updatedInvoice.remainingAmount === 0,
        paymentMethod: updatedInvoice.paymentMethod
      });
    }
  };

  // Status Badge Helper
  const renderStatusBadge = (status) => {
    switch (status) {
      case 'paid':
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold bg-emerald-100 text-emerald-800 border border-emerald-200/60">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-600"></span>
            {isAr ? 'مدفوعة' : 'Payée'}
          </span>
        );
      case 'partial':
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold bg-amber-100 text-amber-800 border border-amber-200/60">
            <span className="w-1.5 h-1.5 rounded-full bg-amber-600"></span>
            {isAr ? 'مدفوعة جزئياً' : 'Partiellement payée'}
          </span>
        );
      case 'unpaid':
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold bg-red-100 text-red-800 border border-red-200/60">
            <span className="w-1.5 h-1.5 rounded-full bg-red-600"></span>
            {isAr ? 'غير مدفوعة' : 'Impayée'}
          </span>
        );
      case 'draft':
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold bg-slate-100 text-slate-700 border border-slate-200/60">
            <span className="w-1.5 h-1.5 rounded-full bg-slate-500"></span>
            {isAr ? 'مسودة' : 'Brouillon'}
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold bg-blue-100 text-blue-800 border border-blue-200/60">
            <span className="w-1.5 h-1.5 rounded-full bg-blue-600"></span>
            {isAr ? 'صادرة' : 'Émise'}
          </span>
        );
    }
  };

  const loading = loadingManual || loadingRentals;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="flex flex-col items-center gap-3">
          <div className="animate-spin rounded-full h-9 w-9 border-3 border-blue-600 border-t-transparent" />
          <span className="text-xs font-bold text-slate-500">{isAr ? 'جاري تحميل الفواتير...' : 'Chargement des factures...'}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      
      {/* ── HEADER & PRIMARY ACTION BUTTON ── */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-black text-slate-900 tracking-tight flex items-center gap-2.5">
            <FileText className="w-7 h-7 text-blue-600" />
            {t('invoices_title') || (isAr ? 'الفواتير' : 'Factures')}
          </h1>
          <p className="text-xs sm:text-sm text-slate-500 mt-1">
            {isAr 
              ? 'إنشاء وإدارة فواتير الشركات والزبائن اليدوية وتلقائية الكراء والتحميل بصيغة PDF' 
              : 'Gestion, édition, encaissement et impression PDF des factures manuelles et de location.'}
          </p>
        </div>

        {/* Clear Button: + Créer une facture manuellement */}
        <button
          type="button"
          onClick={() => {
            setEditingInvoice(null);
            setShowManualModal(true);
          }}
          className="inline-flex items-center justify-center gap-2.5 px-5 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white rounded-xl font-bold text-sm shadow-md shadow-blue-500/25 transition-all transform active:scale-95"
        >
          <Plus className="w-5 h-5 stroke-[2.5]" />
          <span>{t('create_manual_invoice') || (isAr ? 'إنشاء فاتورة يدويًا' : 'Créer une facture manuellement')}</span>
        </button>
      </div>

      {/* ── KPI STATS SUMMARY CARDS ── */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white border border-slate-200/80 rounded-2xl p-4.5 shadow-sm space-y-2">
          <div className="flex items-center justify-between text-slate-500">
            <span className="text-xs font-bold uppercase tracking-wider">{t('kpi_total_invoiced') || 'Total Facturé (TTC)'}</span>
            <div className="p-2 bg-blue-50 text-blue-600 rounded-xl">
              <DollarSign className="w-4 h-4" />
            </div>
          </div>
          <div className="text-xl font-black text-slate-900 font-mono">
            {stats.totalInvoiced.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} <span className="text-xs font-bold text-slate-500">{cur}</span>
          </div>
          <div className="text-[11px] text-slate-400 font-medium">
            {stats.totalCount} {isAr ? 'فاتورة إجمالية' : 'factures au total'}
          </div>
        </div>

        <div className="bg-white border border-slate-200/80 rounded-2xl p-4.5 shadow-sm space-y-2">
          <div className="flex items-center justify-between text-slate-500">
            <span className="text-xs font-bold uppercase tracking-wider">{t('kpi_total_collected') || 'Total Encaissé'}</span>
            <div className="p-2 bg-emerald-50 text-emerald-600 rounded-xl">
              <CheckCircle2 className="w-4 h-4" />
            </div>
          </div>
          <div className="text-xl font-black text-emerald-700 font-mono">
            {stats.totalCollected.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} <span className="text-xs font-bold text-slate-500">{cur}</span>
          </div>
          <div className="text-[11px] text-emerald-600 font-semibold">
            {stats.countPaid} {isAr ? 'فاتورة مسددة بالكامل' : 'factures soldées'}
          </div>
        </div>

        <div className="bg-white border border-slate-200/80 rounded-2xl p-4.5 shadow-sm space-y-2">
          <div className="flex items-center justify-between text-slate-500">
            <span className="text-xs font-bold uppercase tracking-wider">{t('kpi_total_pending') || 'Reste à Recouvrer'}</span>
            <div className="p-2 bg-amber-50 text-amber-600 rounded-xl">
              <Clock className="w-4 h-4" />
            </div>
          </div>
          <div className="text-xl font-black text-amber-600 font-mono">
            {stats.totalPending.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} <span className="text-xs font-bold text-slate-500">{cur}</span>
          </div>
          <div className="text-[11px] text-amber-700 font-semibold">
            {stats.countPartial + stats.countUnpaid} {isAr ? 'فاتورة في الانتظار' : 'factures en attente'}
          </div>
        </div>

        <div className="bg-white border border-slate-200/80 rounded-2xl p-4.5 shadow-sm space-y-2">
          <div className="flex items-center justify-between text-slate-500">
            <span className="text-xs font-bold uppercase tracking-wider">{t('kpi_invoice_count') || 'Factures & Statuts'}</span>
            <div className="p-2 bg-indigo-50 text-indigo-600 rounded-xl">
              <FileText className="w-4 h-4" />
            </div>
          </div>
          <div className="flex items-center gap-2 pt-1">
            <span className="text-xs px-2 py-0.5 bg-emerald-100 text-emerald-800 rounded font-bold">{stats.countPaid} {isAr ? 'مدفوعة' : 'Payées'}</span>
            <span className="text-xs px-2 py-0.5 bg-amber-100 text-amber-800 rounded font-bold">{stats.countPartial} {isAr ? 'جزئية' : 'Partielles'}</span>
          </div>
          <div className="text-[11px] text-slate-400 font-medium">
            {manualInvoices.length} {isAr ? 'يدوية' : 'manuelles'} • {rentals.length} {isAr ? 'كراء' : 'locations'}
          </div>
        </div>
      </div>

      {/* ── TABS, SEARCH & FILTERS BAR ── */}
      <div className="bg-white border border-slate-200/80 rounded-2xl p-4 shadow-sm space-y-4">
        
        {/* Tabs Bar */}
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 pb-3">
          <div className="flex items-center gap-1.5 p-1 bg-slate-100/80 rounded-xl">
            <button
              type="button"
              onClick={() => setActiveTab('all')}
              className={`px-3.5 py-1.5 rounded-lg text-xs font-bold transition-all ${
                activeTab === 'all' ? 'bg-white text-blue-700 shadow-sm' : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              {t('tab_all_invoices') || (isAr ? 'جميع الفواتير' : 'Toutes les factures')} ({manualInvoices.length + rentals.length})
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('manual')}
              className={`px-3.5 py-1.5 rounded-lg text-xs font-bold transition-all ${
                activeTab === 'manual' ? 'bg-white text-blue-700 shadow-sm' : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              🏷️ {t('tab_manual_invoices') || (isAr ? 'فواتير يدوية' : 'Factures Manuelles')} ({manualInvoices.length})
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('rentals')}
              className={`px-3.5 py-1.5 rounded-lg text-xs font-bold transition-all ${
                activeTab === 'rentals' ? 'bg-white text-blue-700 shadow-sm' : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              🚗 {t('tab_rental_invoices') || (isAr ? 'فواتير الكراء' : 'Factures de Location')} ({rentals.length})
            </button>
          </div>

          <div className="text-xs text-slate-500 font-semibold">
            {filteredInvoices.length} {isAr ? 'فاتورة معروضة' : 'facture(s) trouvée(s)'}
          </div>
        </div>

        {/* Search, Status & Sort Row */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-12 gap-3">
          
          {/* Search Box */}
          <div className="md:col-span-5 relative">
            <input
              type="text"
              placeholder={isAr ? 'ابحث برقم الفاتورة، الشركة، الزبون، الهاتف، أو ICE...' : 'Recherche N° facture, société, client, ICE...'}
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              className="w-full pl-9 pr-4 py-2 bg-slate-50 text-sm border border-slate-200 rounded-xl focus:bg-white focus:ring-2 focus:ring-blue-500 transition-all"
            />
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-2.5" />
          </div>

          {/* Status Filter */}
          <div className="md:col-span-4">
            <select
              value={statusFilter}
              onChange={e => setStatusFilter(e.target.value)}
              className="w-full px-3 py-2 bg-slate-50 text-sm font-semibold border border-slate-200 rounded-xl focus:bg-white focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">{isAr ? 'جميع الحالات' : 'Tous les statuts'}</option>
              <option value="paid">{isAr ? '✅ مدفوعة بالكامل' : '✅ Payée'}</option>
              <option value="partial">{isAr ? '⏳ مدفوعة جزئياً' : '⏳ Partiellement payée'}</option>
              <option value="issued">{isAr ? '📤 صادرة' : '📤 Émise'}</option>
              <option value="unpaid">{isAr ? '⚠️ غير مدفوعة' : '⚠️ Impayée'}</option>
              <option value="draft">{isAr ? '📝 مسودة' : '📝 Brouillon'}</option>
            </select>
          </div>

          {/* Sort By */}
          <div className="md:col-span-3">
            <select
              value={sortBy}
              onChange={e => setSortBy(e.target.value)}
              className="w-full px-3 py-2 bg-slate-50 text-sm font-semibold border border-slate-200 rounded-xl focus:bg-white focus:ring-2 focus:ring-blue-500"
            >
              <option value="date_desc">{isAr ? '📅 الأحدث أولاً' : '📅 Plus récentes'}</option>
              <option value="date_asc">{isAr ? '📅 الأقدم أولاً' : '📅 Plus anciennes'}</option>
              <option value="amount_desc">{isAr ? '💰 المبلغ: تنازلي' : '💰 Montant décroissant'}</option>
              <option value="amount_asc">{isAr ? '💰 المبلغ: تصاعدي' : '💰 Montant croissant'}</option>
            </select>
          </div>

        </div>

      </div>

      {/* ── INVOICES DATA TABLE ── */}
      <div className="bg-white rounded-2xl border border-slate-200/80 overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full text-sm text-left">
            <thead className="bg-slate-50/80 border-b border-slate-200 text-[11px] font-bold text-slate-700 uppercase tracking-wider">
              <tr>
                <th className={`px-5 py-3.5 ${isAr ? 'text-right' : 'text-left'}`}>
                  {isAr ? 'رقم الفاتورة والنوع' : 'N° Facture & Type'}
                </th>
                <th className={`px-5 py-3.5 ${isAr ? 'text-right' : 'text-left'}`}>
                  {isAr ? 'الشركة / الزبون' : 'Société / Client'}
                </th>
                <th className={`px-5 py-3.5 ${isAr ? 'text-right' : 'text-left'}`}>
                  {isAr ? 'التاريخ والاستحقاق' : 'Date & Échéance'}
                </th>
                <th className={`px-5 py-3.5 ${isAr ? 'text-left' : 'text-right'}`}>
                  {isAr ? 'المبلغ الإجمالي' : 'Montant TTC'}
                </th>
                <th className={`px-5 py-3.5 ${isAr ? 'text-left' : 'text-right'}`}>
                  {isAr ? 'المتبقي' : 'Reste à payer'}
                </th>
                <th className="px-5 py-3.5 text-center">
                  {isAr ? 'الحالة' : 'Statut'}
                </th>
                <th className={`px-5 py-3.5 ${isAr ? 'text-left' : 'text-right'}`}>
                  {t('col_actions') || (isAr ? 'الإجراءات' : 'Actions')}
                </th>
              </tr>
            </thead>

            <tbody className="divide-y divide-slate-100">
              {filteredInvoices.map((inv) => (
                <tr key={inv.id || inv.invoiceNumber} className="hover:bg-slate-50/70 transition-colors group">
                  
                  {/* N° Facture & Type */}
                  <td className="px-5 py-4">
                    <div className="flex items-center gap-2">
                      <span className="font-mono font-bold text-slate-900">
                        {inv.invoiceNumber}
                      </span>
                      {inv.isManual ? (
                        <span className="px-1.5 py-0.5 bg-purple-50 text-purple-700 text-[10px] font-bold rounded border border-purple-200">
                          {isAr ? 'يدوية' : 'Manuelle'}
                        </span>
                      ) : (
                        <span className="px-1.5 py-0.5 bg-blue-50 text-blue-700 text-[10px] font-bold rounded border border-blue-200">
                          {isAr ? 'كراء' : 'Location'}
                        </span>
                      )}
                    </div>
                  </td>

                  {/* Company / Client */}
                  <td className="px-5 py-4">
                    <div className="font-bold text-slate-900 flex items-center gap-1.5">
                      <Building className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                      <span>{inv.clientOrCompanyName}</span>
                    </div>
                    {(inv.companyICE || inv.ice) && (
                      <div className="text-[11px] font-mono text-blue-700 mt-0.5">
                        ICE: {inv.companyICE || inv.ice}
                      </div>
                    )}
                    {inv.carName && (
                      <div className="text-[11px] text-slate-500 mt-0.5">
                        🚗 {inv.carName}
                      </div>
                    )}
                  </td>

                  {/* Dates */}
                  <td className="px-5 py-4 text-xs text-slate-600">
                    <div className="font-semibold text-slate-900">
                      {formatDateFR(inv.date)}
                    </div>
                    {inv.dueDate && (
                      <div className="text-[11px] text-slate-400 mt-0.5">
                        {isAr ? 'إلى:' : 'Éch:'} {formatDateFR(inv.dueDate)}
                      </div>
                    )}
                  </td>

                  {/* Montant TTC */}
                  <td className={`px-5 py-4 font-mono font-bold text-slate-900 ${isAr ? 'text-left' : 'text-right'}`}>
                    {(inv.totalTTC || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} <span className="text-xs font-semibold text-slate-500">{cur}</span>
                  </td>

                  {/* Reste à payer */}
                  <td className={`px-5 py-4 font-mono font-bold ${isAr ? 'text-left' : 'text-right'}`}>
                    {inv.remainingAmount > 0 ? (
                      <span className="text-amber-600">
                        {inv.remainingAmount.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} <span className="text-xs font-semibold text-slate-500">{cur}</span>
                      </span>
                    ) : (
                      <span className="text-emerald-600 text-xs font-bold">
                        {isAr ? '0.00 (مسدد)' : '0.00 (Soldé)'}
                      </span>
                    )}
                  </td>

                  {/* Status */}
                  <td className="px-5 py-4 text-center">
                    {renderStatusBadge(inv.status)}
                  </td>

                  {/* Actions */}
                  <td className={`px-5 py-4 ${isAr ? 'text-left' : 'text-right'}`}>
                    <div className="flex items-center justify-end gap-1.5 flex-wrap">
                      
                      {/* Voir / Preview */}
                      <button
                        type="button"
                        onClick={() => setPreviewInvoice(inv)}
                        className="p-1.5 text-slate-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition"
                        title={isAr ? 'معاينة الفاتورة' : 'Voir la facture'}
                      >
                        <Eye className="w-4 h-4" />
                      </button>

                      {/* Imprimer / PDF */}
                      <button
                        type="button"
                        onClick={() => printInvoice(inv, settings)}
                        className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg transition"
                        title={isAr ? 'طباعة / تحميل PDF' : 'Imprimer / Télécharger PDF'}
                      >
                        <Printer className="w-4 h-4" />
                      </button>

                      {/* Modifier (for manual invoices) */}
                      {inv.isManual && (
                        <button
                          type="button"
                          onClick={() => {
                            setEditingInvoice(inv);
                            setShowManualModal(true);
                          }}
                          className="p-1.5 text-slate-600 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition"
                          title={isAr ? 'تعديل الفاتورة' : 'Modifier la facture'}
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                      )}

                      {/* Enregistrer un paiement */}
                      {inv.remainingAmount > 0 && (
                        <button
                          type="button"
                          onClick={() => setPaymentInvoice(inv)}
                          className="p-1.5 text-emerald-600 hover:bg-emerald-50 rounded-lg transition"
                          title={isAr ? 'تسجيل دفعة' : 'Enregistrer un paiement'}
                        >
                          <DollarSign className="w-4 h-4" />
                        </button>
                      )}

                      {/* Marquer comme payée */}
                      {inv.remainingAmount > 0 && (
                        <button
                          type="button"
                          onClick={() => handleMarkAsPaid(inv)}
                          className="p-1.5 text-slate-500 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg transition"
                          title={isAr ? 'تحديد كمدفوعة بالكامل' : 'Marquer comme payée'}
                        >
                          <CheckCircle2 className="w-4 h-4" />
                        </button>
                      )}

                      {/* Dupliquer (for manual invoices) */}
                      {inv.isManual && (
                        <button
                          type="button"
                          onClick={() => handleDuplicateInvoice(inv)}
                          className="p-1.5 text-slate-500 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition"
                          title={isAr ? 'نسخ الفاتورة' : 'Dupliquer la facture'}
                        >
                          <Copy className="w-4 h-4" />
                        </button>
                      )}

                      {/* Supprimer (for manual invoices) */}
                      {inv.isManual && (
                        <button
                          type="button"
                          onClick={() => handleDeleteInvoice(inv)}
                          className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition"
                          title={isAr ? 'حذف الفاتورة' : 'Supprimer'}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}

                    </div>
                  </td>

                </tr>
              ))}

              {filteredInvoices.length === 0 && (
                <tr>
                  <td colSpan={7} className="text-center py-12 text-slate-400">
                    <div className="flex flex-col items-center justify-center gap-2">
                      <FileText className="w-8 h-8 text-slate-300 stroke-[1.5]" />
                      <div className="text-sm font-semibold">{t('no_invoices_found') || (isAr ? 'لا توجد فواتير' : 'Aucune facture trouvée')}</div>
                      <button
                        type="button"
                        onClick={() => {
                          setEditingInvoice(null);
                          setShowManualModal(true);
                        }}
                        className="mt-2 text-xs font-bold text-blue-600 hover:underline"
                      >
                        + {isAr ? 'إنشاء أول فاتورة يدوية' : 'Créer une première facture manuelle'}
                      </button>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── MODALS ── */}

      {/* 1. Manual Invoice Modal (Create / Edit) */}
      {showManualModal && (
        <ManualInvoiceModal
          invoice={editingInvoice}
          clients={clients}
          rentals={rentals}
          settings={settings}
          existingCount={manualInvoices.length}
          onSave={handleSaveManualInvoice}
          onClose={() => {
            setShowManualModal(false);
            setEditingInvoice(null);
          }}
        />
      )}

      {/* 2. In-App Invoice Preview Modal */}
      {previewInvoice && (
        <InvoicePreviewModal
          invoice={previewInvoice}
          settings={settings}
          onEdit={(inv) => {
            setEditingInvoice(inv);
            setShowManualModal(true);
          }}
          onRecordPayment={(inv) => {
            setPaymentInvoice(inv);
          }}
          onClose={() => setPreviewInvoice(null)}
        />
      )}

      {/* 3. Record Payment Modal */}
      {paymentInvoice && (
        <RecordPaymentModal
          invoice={paymentInvoice}
          settings={settings}
          onSave={handleRecordPaymentSave}
          onClose={() => setPaymentInvoice(null)}
        />
      )}

    </div>
  );
}
